import Vue from 'vue';
import App from './app.vue';
import router from './router';
import store from './store';
import Vuelidate from 'vuelidate';
import PerfectScrollbar from 'vue2-perfect-scrollbar';
import 'vue2-perfect-scrollbar/dist/vue2-perfect-scrollbar.css';
import {ToastPlugin} from 'bootstrap-vue';
import {OvToastPlugin} from './plugins/ovToastPlugin';
import axios from 'axios';
import VueI18n from 'vue-i18n';
import {defaultLocale, messages} from '@/i18n';
import {clearTokens, getStoredToken, refreshAccessToken, startKeepAlive} from '@/services/keycloak';

export const deanDialogBus = new Vue();

Vue.use(VueI18n);
Vue.use(ToastPlugin);
Vue.use(PerfectScrollbar);
Vue.use(Vuelidate);
Vue.use(OvToastPlugin);

Vue.config.productionTip = false;
store.dispatch('reqTheme');

// Start the Keycloak keep-alive mechanism as soon as the app boots, if a session already
// exists (eg. after a hard refresh). It's also (re)started right after a successful login,
// see router.beforeEach below.
if (process.env.VUE_APP_KEYCLOAK_ENABLED === 'true' && getStoredToken()) {
  startKeepAlive();
}

// src: https://alligator.io/vuejs/vue-router-modify-head/
// This callback runs before every route change, including on page load.
// @ts-ignore
router.beforeEach((to, from, next) => {
  // Legacy hash-style deep links (eg. /dashboard/oneviewDashboard#/orders/...) -> real path.
  if (to.hash && to.hash.startsWith('#/')) {
    next(to.hash.substring(1));
    return;
  }

  if (!store.getters?.isAuthenticated) {
    if (to.meta?.requiresAuth !== false) {
      store.dispatch('setLoadOverlay', true);
      store.dispatch('fetchLoggedInUser').then(
        // @ts-ignore
        (response) => {
          store.dispatch('reqTheme');
          if (process.env.VUE_APP_KEYCLOAK_ENABLED === 'true') {
            startKeepAlive();
          }
          if (to.fullPath === '/') {
            next({name: 'dashboard'});
          }
        },
        // @ts-ignore
        (error) => {
          // Remember the deep link so the user lands there after logging in.
          if (to.fullPath !== '/' && to.meta?.requiresAuth !== false) {
            sessionStorage.setItem('redirect_after_login', to.fullPath);
          }
          next({name: 'auth.login'});
        },
      ).finally(
        () => {
          store.dispatch('setLoadOverlay', false);
          // This goes through the matched routes from last to first, finding the closest route with a title.
          // eg. if we have /some/deep/nested/route and /some, /deep, and /nested have titles, nested's will be chosen.
          // @ts-ignore
          const nearestWithTitle = to.matched.slice().reverse().find((r) => r.meta && r.meta.title);

          // Find the nearest route element with meta tags.
          // @ts-ignore
          const nearestWithMeta = to.matched.slice().reverse().find((r) => r.meta && r.meta.metaTags);
          // @ts-ignore
          const previousNearestWithMeta = from.matched.slice().reverse().find((r) => r.meta && r.meta.metaTags);

          // If a route with a title was found, set the document (page) title to that value.
          if (nearestWithTitle) {
            document.title = nearestWithTitle.meta.title + ' - ' + store.getters.getTheme.data.title;
          }

          // Remove any stale meta tags from the document using the key attribute we set below.
          // @ts-ignore
          Array.from(
            document.querySelectorAll('[data-vue-router-controlled]'),
          ).map((el) => el?.parentNode?.removeChild(el));

          // Skip rendering meta tags if there are none.
          if (!nearestWithMeta) {
            return next();
          }

          // Turn the meta tag definitions into actual elements in the head.
          // @ts-ignore
          nearestWithMeta.meta.metaTags.map((tagDef) => {
            const tag = document.createElement('meta');

            Object.keys(tagDef).forEach((key) => {
              tag.setAttribute(key, tagDef[key]);
            });

            // We use this to track which meta tags we create, so we don't interfere with other ones.
            tag.setAttribute('data-vue-router-controlled', '');

            return tag;
          })
            // Add the meta tags to the document head.
            // @ts-ignore
            .forEach((tag) => document.head.appendChild(tag));
        },
      );
    } else {
      next();
    }
  } else if (to.meta?.guestOnly) {
    // Authenticated users should never see the login/activation/reset-password
    // screens — send them straight to the dashboard instead.
    next({name: 'dashboard'});
  } else {
    next();
  }
});

router.afterEach((to: any, from: any) => {
  store.dispatch('requestHeader');
});

// Request interceptor - add Bearer token to every API call (except Keycloak token endpoint)
axios.interceptors.request.use((config: any) => {
  const keycloakUrl = process.env.VUE_APP_KEYCLOAK_URL;
  if (keycloakUrl && config.url && config.url.startsWith(keycloakUrl)) {
    return config;
  }
  const token = getStoredToken();
  if (token) {
    config.headers = config.headers || {};
    config.headers['Authorization'] = `Bearer ${token}`;
  }
  return config;
});

// Response interceptor — on 401 try refresh token, if not then logout
// @ts-ignore
axios.interceptors.response.use(null, async (error: any) => {
  const original = error.config;
  // auth.login / auth.check errors (eg. invalid credentials) are expected
  // login failures, not an expired session — let the caller handle them.
  if (original?.url && (original.url.indexOf('/auth.login') !== -1 || original.url.indexOf('/auth.check') !== -1)) {
    return Promise.reject(error);
  }

  if (error.response?.status === 401) {
    const keycloakEnabled = process.env.VUE_APP_KEYCLOAK_ENABLED === 'true';
    if (keycloakEnabled && !original._retry) {
      original._retry = true;
      const newToken = await refreshAccessToken();
      if (newToken) {
        original.headers = original.headers || {};
        original.headers['Authorization'] = `Bearer ${newToken}`;
        return axios(original);
      }
    }

    deanDialogBus.$emit('close');

    // This is a WS Portal-side session timeout/invalid token (not an explicit
    // user logout), so we must only end the WS Portal session locally here.
    // We intentionally do NOT redirect to Keycloak's end_session endpoint —
    // doing so would terminate the shared Keycloak SSO session as well,
    // logging the user out of other Keycloak-backed apps too (see TC5).
    // The Keycloak SSO session must remain active; only WS Portal's local
    // token/auth state is cleared, so the user can silently reuse the still
    // active SSO session next time they log in.
    if (keycloakEnabled) {
      clearTokens();
    }

    store.commit('SET_IS_AUTHENTICATED', false);
    window.location.href = `${window.location.origin}${router.resolve({name: 'auth.login'}).href}`;
  }

  if (error.response?.status === 403) {
    router.go(-1);
  }

  return Promise.reject(error);
});

export const i18n = new VueI18n({
  messages,
  locale: defaultLocale,
  fallbackLocale: defaultLocale,
});

new Vue({
  i18n,
  router,
  store,
  render: (h) => h(App),
}).$mount('#app');
